// Display helpers for admin device status / online / recording. Pure presentation;
// values observed live (FACTS PACK §3c) plus the lifecycle states the action bar drives.
// Self-contained so the devices feature does not couple to other features.

import type { AdminDevice } from './devicesApi';

export type Tone = 'good' | 'warn' | 'danger' | 'neutral' | 'info';

// ---- device status ----

const DEVICE_STATUS_LABEL: Record<string, string> = {
  active: '正常',
  enabled: '正常',
  disabled: '已停用',
  lost: '已掛失',
  reported_lost: '已掛失',
  suspended: '已暫停',
};

const DEVICE_STATUS_TONE: Record<string, Tone> = {
  active: 'good',
  enabled: 'good',
  disabled: 'warn',
  lost: 'danger',
  reported_lost: 'danger',
  suspended: 'warn',
};

export function deviceStatusLabel(status: string | undefined): string {
  if (!status) return '—';
  return DEVICE_STATUS_LABEL[status] ?? status;
}

export function deviceStatusTone(status: string | undefined): Tone {
  if (!status) return 'neutral';
  return DEVICE_STATUS_TONE[status] ?? 'neutral';
}

// ---- online state ----

export function onlineLabel(d: AdminDevice): string {
  const s = d.online?.state;
  if (s === 'online') return '在線';
  if (s === 'offline') return '離線';
  return s ?? '—';
}

export function onlineTone(d: AdminDevice): Tone {
  const s = d.online?.state;
  if (s === 'online') return d.online?.stale_hint ? 'warn' : 'good';
  if (s === 'offline') return 'neutral';
  return 'neutral';
}

// ---- recording ----

const RECORDING_MODE_LABEL: Record<string, string> = {
  continuous: '連續錄影',
  event: '事件錄影',
  motion: '位移錄影',
  off: '關閉',
};

export function recordingModeLabel(mode: string | undefined): string {
  if (!mode) return '—';
  return RECORDING_MODE_LABEL[mode] ?? mode;
}

const RECORDING_LOCATION_LABEL: Record<string, string> = {
  cloud: '雲端',
  sdcard: 'SD 卡',
  local: '本機',
};

export function recordingLocationLabel(loc: string | undefined): string {
  if (!loc) return '—';
  return RECORDING_LOCATION_LABEL[loc] ?? loc;
}

export function recordingSummary(d: AdminDevice): string {
  const mode = d.recording?.mode;
  if (!mode || mode === 'off') return '未錄影';
  const parts = [recordingModeLabel(mode)];
  if (d.recording?.location) parts.push(recordingLocationLabel(d.recording.location));
  if (d.recording?.quality) parts.push(d.recording.quality);
  return parts.join('・');
}

// ---- roles ----

const ROLE_LABEL: Record<string, string> = {
  owner: '擁有者',
  shared: '共享',
  admin: '管理員',
  viewer: '檢視者',
};

export function deviceRoleLabel(role: string | undefined): string {
  if (!role) return '—';
  return ROLE_LABEL[role] ?? role;
}

// ---- subscription ----

const SUB_STATUS_LABEL: Record<string, string> = {
  active: '生效中',
  expired: '已到期',
  cancelled: '已取消',
  none: '無',
  trial: '試用中',
};

export function subStatusLabel(status: string | undefined): string {
  if (!status) return '—';
  return SUB_STATUS_LABEL[status] ?? status;
}

export function subStatusTone(status: string | undefined): Tone {
  if (status === 'active' || status === 'trial') return 'good';
  if (status === 'expired') return 'warn';
  if (status === 'cancelled') return 'neutral';
  return 'neutral';
}

export function deviceName(d: AdminDevice): string {
  return d.display_name || d.device_id;
}

// ---- owner ecosystem / brand family ----
// device → owner → user.family_id → families.name (surfaced by admin browse).
// Shows the friendly name; falls back to the raw family id; '—' when the device
// has no owner (unassigned / orphan). fam-platform is the first-party ecosystem.

export function ownerFamilyLabel(d: AdminDevice): string {
  return d.owner_family_name || d.owner_family_id || '—';
}

// A device on the default platform family is first-party ('直營'); anything else is
// a distinct brand ecosystem. Drives a subtle tone so white-label devices stand out.
export function ownerFamilyIsFirstParty(d: AdminDevice): boolean {
  const id = d.owner_family_id;
  return !id || id === 'fam-platform';
}

// ---- lifecycle action gating ----
// Server enforces (requireAdmin); UI gating just hides obvious no-ops.
// status values 'active' observed live; disabled/lost inferred from action results.

export interface DeviceActionGate {
  disable: boolean;
  enable: boolean;
  reportLost: boolean;
}

export function deviceActionGate(d: AdminDevice | null): DeviceActionGate {
  const status = d?.status;
  const disabled = status === 'disabled';
  const lost = status === 'lost' || status === 'reported_lost';
  return {
    // disable: any device not already disabled and not lost
    disable: !disabled && !lost,
    // enable: only a disabled device (re-activates)
    enable: disabled,
    // report-lost: any device not already marked lost
    reportLost: !lost,
  };
}
